import React from 'react';
import { Link } from 'react-router-dom';
import BlogList from '../../components/Blog/BlogList';
import BlogHeader from '../../components/Blog/BlogHeader';
import { getBlogPosts } from '../../contentful';
import '../../styles/blog-overrides.scss';
import Footer from '../../components/Footer';

const BlogPage = () => {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    // Mock data for testing - moved inside useEffect to fix ESLint warning
    const mockPosts = [
      {
        id: '1',
        slug: 'test-post-1',
        title: 'When Good Plans Go Sideways: Our Unplanned Descent Down the Wilderness Channel',
        description: 'A thrilling adventure story about unexpected challenges and how we overcame them in the wilderness.',
        publishedAt: '2025-08-06',
        author: 'Andy Cary',
        tags: ['Adventure Stories', 'Wilderness', 'Challenges'],
        featuredImage: {
          url: 'https://via.placeholder.com/800x400/3b82f6/ffffff?text=Adventure+Story',
          alt: 'Wilderness adventure'
        }
      },
      {
        id: '2',
        slug: 'test-post-2',
        title: 'Building Better Apps: A Developer\'s Journey',
        description: 'Insights and lessons learned from developing mobile applications that make a difference.',
        publishedAt: '2025-08-05',
        author: 'Andy Cary',
        tags: ['App Development', 'Technology', 'Mobile'],
        featuredImage: {
          url: 'https://via.placeholder.com/800x400/8b5cf6/ffffff?text=App+Development',
          alt: 'App development'
        }
      }
    ];
    
    document.body.classList.add('blog-page');
    document.documentElement.classList.add('blog-page');

    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const blogPosts = await getBlogPosts();
        
        // If no posts from Contentful, use mock data
        if (blogPosts && blogPosts.length > 0) {
          setPosts(blogPosts);
        } else {
          setPosts(mockPosts);
        }
      } catch (error) {
        setPosts(mockPosts);
        // Don't set error, just use mock data
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();

    return () => {
      document.body.classList.remove('blog-page');
      document.documentElement.classList.remove('blog-page');
    };
  }, []); // Removed mockPosts from dependencies since it's now inside useEffect

  if (loading) {
    return (
      <div className="min-h-screen bg-background-dark">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-accent/20 border-t-accent" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-dark">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <div className="text-red-500">
              <div className="w-16 h-16 bg-gradient-to-r from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-red-700 mb-4">Error loading posts</h3>
              <p className="text-base text-red-500 max-w-md mx-auto leading-relaxed">
                {error.message || 'Something went wrong while loading the blog posts. Please try again later.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark">
      <BlogHeader />
      <div className="pt-24 sm:pt-28 lg:pt-32">
        {/* Blog Content Section */}
        <section className="py-4 sm:py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-3 sm:px-6">
            {posts.length > 0 ? (
              <div className="space-y-8 sm:space-y-12">
                {/* Featured Post */}
                {posts.length > 0 && (
                  <div className="mb-8 sm:mb-12">
                    <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-slate-100 mb-6 sm:mb-8 text-center">
                      Featured Story
                    </h2>
                    
                    <Link to={`/blog/${posts[0].slug}`} className="block">
                      <div className="group cursor-pointer">
                        <div className="bg-slate-800/30 rounded-2xl sm:rounded-3xl shadow-xl border border-accent/10 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:border-accent/20">
                          {posts[0].featuredImage && (
                            <div className="relative h-48 sm:h-80 lg:h-[500px] overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10"></div>
                              <img
                                src={posts[0].featuredImage.url}
                                alt={posts[0].featuredImage.alt}
                                className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                                loading="lazy"
                              />
                              <div className="absolute top-3 sm:top-6 left-3 sm:left-6 bg-accent text-background-dark px-2 sm:px-4 py-1 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold shadow-lg">
                                ✨ FEATURED
                              </div>
                            </div>
                          )}
                          
                          <div className="p-4 sm:p-8 lg:p-12">
                            {posts[0].tags && posts[0].tags.length > 0 && (
                              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
                                {posts[0].tags.slice(0, 3).map((tag, tagIndex) => (
                                  <span
                                    key={tagIndex}
                                    className="px-2 sm:px-4 py-1 sm:py-2 bg-accent/10 text-accent text-xs sm:text-sm font-semibold rounded-full border border-accent/20"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            <h3 className="text-lg sm:text-2xl lg:text-4xl font-bold text-slate-100 mb-4 sm:mb-6 group-hover:text-accent transition-colors duration-300 leading-tight">
                              {posts[0].title}
                            </h3>
                            
                            <p className="text-slate-400 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 leading-relaxed">
                              {posts[0].description || 'Discover valuable insights and updates. Click to read the full article and learn more.'}
                            </p>
                            
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-slate-400 gap-2 sm:gap-0">
                              <div className="flex items-center space-x-2 sm:space-x-3">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-accent rounded-full flex items-center justify-center text-background-dark text-xs sm:text-sm font-bold">
                                  {posts[0].author ? posts[0].author.charAt(0).toUpperCase() : 'A'}
                                </div>
                                <span className="font-medium">{posts[0].author}</span>
                                <span className="hidden sm:inline">•</span>
                                <span>{new Date(posts[0].publishedAt).toLocaleDateString()}</span>
                              </div>
                              <span className="text-accent font-medium">Read Full Story →</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                )}

                {/* All Posts Section */}
                <div>
                  <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-slate-100 mb-6 sm:mb-8 text-center">
                    All Posts
                  </h2>
                  <BlogList posts={posts} featuredPostId={posts[0]?.id} />
                </div>
              </div>
            ) : (
              <div className="text-center py-16 sm:py-20">
                <div className="text-slate-400">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-slate-300 mb-4">No posts yet</h3>
                  <p className="text-base sm:text-lg text-slate-400 max-w-md mx-auto leading-relaxed">
                    Check back soon for new content and updates.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPage; 